import React, { useState, useEffect, useCallback } from "react";
import { fetchMonitoringAlerts, fetchMonitoringWatchlist, resolveAlert } from "../../../lib/researchApi";
import SeverityBadge from "../shared/SeverityBadge";
import GovernanceBadge from "../shared/GovernanceBadge";

const STRATEGY_LABELS = {
    wheel:           "Wheel",
    earnings:        "Earnings",
    tactical_spread: "Tactical Spread",
    regime_spread:   "Regime Spread",
};

const ALERT_TYPE_LABELS = {
    overfit_spike:                 "Overfit Spike",
    ruin_probability_rising:       "Ruin Probability Rising",
    walk_forward_instability:      "Walk-Forward Instability",
    deployment_readiness_drop:     "Deployment Readiness Drop",
    validation_health_drop:        "Validation Health Drop",
    operational_confidence_drop:   "Operational Confidence Drop",
    unstable_parameter_behavior:   "Unstable Parameter Behavior",
    degraded_strategy_detected:    "Strategy Degraded",
    excessive_validation_drift:    "Excessive Validation Drift",
};

function fmtVal(v) {
    if (v == null) return "—";
    return typeof v === "number" ? v.toFixed(3) : v;
}

function AlertRow({ alert, onResolve, resolving }) {
    const ts = alert.last_detected_at
        ? new Date(alert.last_detected_at).toLocaleString("en-US", { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" })
        : "—";
    return (
        <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.05)" }}>
            <td style={{ padding: "10px 8px" }}>
                <SeverityBadge severity={alert.severity} />
            </td>
            <td style={{ padding: "10px 8px", fontSize: "12px", color: "var(--savvy-text)", fontWeight: 500 }}>
                {STRATEGY_LABELS[alert.strategy] || alert.strategy}
            </td>
            <td style={{ padding: "10px 8px", fontSize: "12px", color: "var(--savvy-text)" }}>
                {ALERT_TYPE_LABELS[alert.alert_type] || alert.alert_type}
            </td>
            <td style={{ padding: "10px 8px", fontSize: "11px", color: "var(--savvy-muted)", maxWidth: 280 }}>
                {alert.message}
            </td>
            <td style={{ padding: "10px 8px", fontSize: "11px", color: "var(--savvy-muted)", whiteSpace: "nowrap" }}>
                {alert.current_value != null ? (
                    <span>
                        <span style={{ color: "var(--savvy-text)", fontWeight: 600 }}>{fmtVal(alert.current_value)}</span>
                        {alert.threshold_value != null ? <span> / {fmtVal(alert.threshold_value)}</span> : null}
                    </span>
                ) : "—"}
            </td>
            <td style={{ padding: "10px 8px", fontSize: "11px", color: "var(--savvy-dim)", whiteSpace: "nowrap" }}>
                {ts}
            </td>
            <td style={{ padding: "10px 8px" }}>
                {alert.alert_frequency > 1 && (
                    <span style={{ fontSize: "10px", color: "var(--savvy-muted)", background: "rgba(255,255,255,0.06)", borderRadius: 4, padding: "2px 6px" }}>
                        ×{alert.alert_frequency}
                    </span>
                )}
            </td>
            <td style={{ padding: "10px 8px" }}>
                <button
                    onClick={() => onResolve(alert.id)}
                    disabled={resolving === alert.id}
                    style={{
                        background: "none",
                        border: "1px solid rgba(255,255,255,0.15)",
                        color: "var(--savvy-muted)",
                        borderRadius: 4,
                        padding: "2px 8px",
                        fontSize: "11px",
                        cursor: "pointer",
                    }}
                >
                    {resolving === alert.id ? "…" : "Resolve"}
                </button>
            </td>
        </tr>
    );
}

export default function AlertsSection() {
    const [alerts, setAlerts] = useState([]);
    const [watchlist, setWatchlist] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [severityFilter, setSeverityFilter] = useState("");
    const [showResolved, setShowResolved] = useState(false);
    const [resolving, setResolving] = useState(null);

    const load = useCallback(() => {
        setLoading(true);
        setError(null);
        Promise.all([
            fetchMonitoringAlerts({ severity: severityFilter || undefined, include_resolved: showResolved }),
            fetchMonitoringWatchlist(),
        ])
            .then(([a, w]) => {
                setAlerts(a.alerts || []);
                setWatchlist(w.watchlist || []);
            })
            .catch((e) => setError(e?.response?.data?.error || "Failed to load alerts"))
            .finally(() => setLoading(false));
    }, [severityFilter, showResolved]);

    useEffect(() => { load(); }, [load]);

    const handleResolve = async (alertId) => {
        setResolving(alertId);
        try {
            await resolveAlert(alertId);
            setAlerts(prev => prev.filter(a => a.id !== alertId));
        } catch {
            // silently fail; they can retry
        } finally {
            setResolving(null);
        }
    };

    const critical = alerts.filter(a => a.severity === "critical");
    const warning = alerts.filter(a => a.severity === "warning");

    return (
        <div>
            {/* Watchlist summary */}
            {watchlist.length > 0 && (
                <div style={{
                    background: "rgba(245,158,11,0.07)",
                    border: "1px solid rgba(245,158,11,0.25)",
                    borderRadius: 8,
                    padding: "10px 16px",
                    marginBottom: 16,
                    display: "flex",
                    gap: 16,
                    alignItems: "center",
                    flexWrap: "wrap",
                }}>
                    <span style={{ fontSize: "11px", fontWeight: 700, color: "#f59e0b", textTransform: "uppercase", letterSpacing: "0.05em" }}>
                        Watchlist
                    </span>
                    {watchlist.map(w => (
                        <div key={w.strategy} style={{ display: "flex", alignItems: "center", gap: 6 }}>
                            <span style={{ fontSize: "12px", color: "var(--savvy-text)" }}>{STRATEGY_LABELS[w.strategy] || w.strategy}</span>
                            <GovernanceBadge state={w.governance_state} />
                        </div>
                    ))}
                </div>
            )}

            {/* Alert counters */}
            <div style={{ display: "flex", gap: 12, marginBottom: 16, alignItems: "center", flexWrap: "wrap" }}>
                <div style={{
                    padding: "6px 14px",
                    background: critical.length > 0 ? "rgba(220,38,38,0.12)" : "rgba(255,255,255,0.04)",
                    border: `1px solid ${critical.length > 0 ? "rgba(220,38,38,0.4)" : "rgba(255,255,255,0.1)"}`,
                    borderRadius: 6,
                    fontSize: "12px",
                    color: critical.length > 0 ? "#f87171" : "var(--savvy-muted)",
                    fontWeight: 600,
                }}>
                    {critical.length} Critical
                </div>
                <div style={{
                    padding: "6px 14px",
                    background: warning.length > 0 ? "rgba(245,158,11,0.1)" : "rgba(255,255,255,0.04)",
                    border: `1px solid ${warning.length > 0 ? "rgba(245,158,11,0.35)" : "rgba(255,255,255,0.1)"}`,
                    borderRadius: 6,
                    fontSize: "12px",
                    color: warning.length > 0 ? "#fbbf24" : "var(--savvy-muted)",
                    fontWeight: 600,
                }}>
                    {warning.length} Warning
                </div>

                <div style={{ marginLeft: "auto", display: "flex", gap: 8 }}>
                    <select
                        value={severityFilter}
                        onChange={e => setSeverityFilter(e.target.value)}
                        style={{
                            background: "var(--savvy-panel)",
                            border: "1px solid var(--savvy-border)",
                            borderRadius: 6,
                            color: "var(--savvy-text)",
                            fontSize: "12px",
                            padding: "4px 8px",
                        }}
                    >
                        <option value="">All severities</option>
                        <option value="critical">Critical only</option>
                        <option value="warning">Warning only</option>
                        <option value="info">Info only</option>
                    </select>
                    <label style={{ display: "flex", alignItems: "center", gap: 6, fontSize: "12px", color: "var(--savvy-muted)", cursor: "pointer" }}>
                        <input
                            type="checkbox"
                            checked={showResolved}
                            onChange={e => setShowResolved(e.target.checked)}
                        />
                        Show resolved
                    </label>
                </div>
            </div>

            {loading && <div style={{ color: "var(--savvy-muted)", fontSize: "13px", padding: "12px 0" }}>Loading alerts…</div>}
            {error && <div style={{ color: "#f87171", fontSize: "13px" }}>Error: {error}</div>}

            {!loading && !error && (
                alerts.length === 0 ? (
                    <div style={{
                        padding: "32px 16px",
                        textAlign: "center",
                        color: "var(--savvy-muted)",
                        fontSize: "13px",
                        background: "var(--savvy-panel)",
                        borderRadius: 8,
                        border: "1px solid var(--savvy-border)",
                    }}>
                        No active alerts
                    </div>
                ) : (
                    <div style={{ overflowX: "auto" }}>
                        <table style={{ width: "100%", borderCollapse: "collapse" }}>
                            <thead>
                                <tr style={{ borderBottom: "1px solid rgba(255,255,255,0.1)" }}>
                                    {["Severity", "Strategy", "Type", "Message", "Value / Threshold", "Last Detected", "Freq", ""].map(h => (
                                        <th key={h} style={{ textAlign: "left", fontSize: "10px", fontWeight: 600, color: "var(--savvy-muted)", textTransform: "uppercase", letterSpacing: "0.05em", padding: "8px 8px", whiteSpace: "nowrap" }}>
                                            {h}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {alerts.map(alert => (
                                    <AlertRow key={alert.id} alert={alert} onResolve={handleResolve} resolving={resolving} />
                                ))}
                            </tbody>
                        </table>
                    </div>
                )
            )}
        </div>
    );
}
